import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getProfileScore, selectProfileScore } from "../../store/slices/aiSlice";
import { RefreshCw, CheckCircle2, AlertCircle, Trophy, Loader2 } from "lucide-react";

const TIERS = [
  { min: 85, label: "Launch-ready", color: "#10b981", bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200", track: "#d1fae5" },
  { min: 70, label: "Strong",       color: "#9333ea", bg: "bg-purple-50",    text: "text-purple-700",    border: "border-purple-200",    track: "#f3e8ff" },
  { min: 50, label: "Needs polish", color: "#f59e0b", bg: "bg-amber-50",   text: "text-amber-700",   border: "border-amber-200",   track: "#fef3c7" },
  { min: 0,  label: "Incomplete",   color: "#f43f5e", bg: "bg-rose-50",    text: "text-rose-700",    border: "border-rose-200",     track: "#ffe4e6" },
];

const getTier = (score) => TIERS.find((t) => score >= t.min) || TIERS[3];

export default function AIProfileScoreCard({ serviceType, serviceId }) {
  const dispatch = useDispatch();
  const { data, loading, error } = useSelector(selectProfileScore);

  const refresh = () => {
    if (serviceType && serviceId) dispatch(getProfileScore({ serviceType, serviceId }));
  };

  useEffect(refresh, [serviceType, serviceId, dispatch]);

  if (!serviceType || !serviceId) return null;

  const score = data?.score ?? 0;
  const tier = getTier(score);
  const R = 52;
  const C = 2 * Math.PI * R;
  const offset = C - (score / 100) * C;
  const missingItems = data?.missingItems || [];
  const strengths = data?.strengths || [];

  return (
    <div className="bg-white rounded-2xl border border-gray-200/70 shadow-[0_1px_3px_rgba(16,24,40,0.04)] overflow-hidden">
      {/* Top section with ring */}
      <div className="relative px-6 pt-5 pb-5 text-center bg-gradient-to-br from-purple-50 via-white to-pink-50">
        <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-3">Profile score</p>
        {/* Refresh button */}
        <button
          onClick={refresh}
          disabled={loading}
          className="absolute top-3.5 right-3.5 flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white hover:border-purple-200 hover:bg-purple-50 transition-colors"
          title="Refresh score"
        >
          {loading
            ? <Loader2 size={15} className="animate-spin text-gray-400" />
            : <RefreshCw size={15} className="text-gray-400" />
          }
        </button>

        {/* Ring */}
        <div className="relative w-28 h-28 mx-auto mb-4">
          <svg className="w-28 h-28 -rotate-90" viewBox="0 0 120 120">
            <circle cx="60" cy="60" r={R} fill="none" strokeWidth="9" stroke={tier.track} />
            <circle
              cx="60" cy="60" r={R} fill="none" strokeWidth="9"
              strokeLinecap="round"
              stroke={tier.color}
              strokeDasharray={C}
              strokeDashoffset={loading ? C : offset}
              style={{ transition: "stroke-dashoffset 0.8s ease" }}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="font-['Manrope'] text-3xl font-extrabold text-gray-900 leading-none tabular-nums">
              {loading ? "—" : score}
            </span>
            <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wider mt-0.5">
              / 100
            </span>
          </div>
        </div>

        {/* Label badge */}
        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${tier.bg} ${tier.text} border ${tier.border}`}>
          <Trophy size={12} />
          {data?.label || tier.label}
        </span>
      </div>

      {/* Divider */}
      <div className="h-px bg-gray-100" />

      {/* Details section */}
      <div className="px-5 py-4 space-y-3 max-h-64 overflow-y-auto">
        {/* Missing items */}
        {missingItems.length > 0 && (
          <div>
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">
              To improve
            </p>
            <ul className="space-y-1.5">
              {missingItems.slice(0, 5).map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-xs leading-snug text-gray-600">
                  <AlertCircle size={13} className="text-amber-500 mt-px flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
              {missingItems.length > 5 && (
                <li className="text-[11px] text-gray-400 pl-5">
                  +{missingItems.length - 5} more
                </li>
              )}
            </ul>
          </div>
        )}

        {/* Strengths */}
        {strengths.length > 0 && (
          <div>
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">
              Completed
            </p>
            <ul className="space-y-1.5">
              {strengths.slice(0, 4).map((s, i) => (
                <li key={i} className="flex items-start gap-2 text-xs leading-snug text-gray-600">
                  <CheckCircle2 size={13} className="text-emerald-500 mt-px flex-shrink-0" />
                  <span>{s}</span>
                </li>
              ))}
              {strengths.length > 4 && (
                <li className="text-[11px] text-gray-400 pl-5">
                  +{strengths.length - 4} more
                </li>
              )}
            </ul>
          </div>
        )}

        {/* No data yet */}
        {!loading && !data && !error && (
          <p className="text-xs text-gray-400 text-center py-2">
            Select a service to see the score
          </p>
        )}
      </div>
    </div>
  );
}
